import React, { createRef, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { PortalContextProvider } from '@amzn/storm-ui';
import { SIDEBAR_TYPE } from '@amzn/aac-chrome-internal-api';
import { NavWrapper, Overlay } from './components/styles';
import { MobileNavSheet } from './components/MobileSideNav/MobileNavSheet';
import {
  selectIsMobileSideNavOpen,
  selectViewType,
  toggleMobileSideNav,
  VIEW_TYPE,
} from 'src/app/slice';
import { RootState, store } from 'src/app/store';
import { ConnectedMobileSideNav } from './components/MobileSideNav/MobileSideNav';
import {
  selectActiveCategoryObject,
  selectLandingAppName,
  selectToolbarItems,
  ToolbarItemProps,
  updateActiveCategory as dispatchUpdateActiveCategory,
  updateLandingAppName,
  updateSelectedNavItem,
} from './store/slice';
import {
  useDomRefById,
  useNavLayout,
  useObserveUrlChange,
} from 'src/commons/hooks';
import { Components, CONSOLE_HEADER_SECTION_ID } from 'src/commons/constants';
import { shouldRenderMenu } from 'src/commons/utils/helpers';
import { markComponentReady } from 'src/commons/utils/logWithTakt';
import { SidenavBundleProvider } from 'src/providers/PantherLocalizationProvider';
import {
  initAjaxNavState,
  initInternalNavState,
  initInternalNavStateWithBackupToolbar,
  NavState,
  updateToolbarItemsWithBackup,
} from 'src/features/nav/store/slice';
import { getNavState } from 'src/commons/apis';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { ConsolidatedSideNav } from 'src/features/nav/components/ConsolidatedSideNav/ConsolidatedSideNav';

export const Nav: React.VFC<{
  viewType: VIEW_TYPE;
  isMobileNavOpen: boolean;
  activeCategoryObject?: ToolbarItemProps;
  toolbarItems?: Array<ToolbarItemProps>;
  navData?: NavState;
  updateActiveCategory: (...args: any[]) => any;
  toggleMobileSideNav: () => void;
  landingAppName: string;
  updateLandingAppName: (...args: any[]) => any;
  updateSelectedNavItem: (...args: any[]) => any;
}> = ({
  viewType,
  isMobileNavOpen,
  activeCategoryObject,
  toolbarItems,
  navData,
  updateActiveCategory,
  toggleMobileSideNav,
  landingAppName,
  updateLandingAppName,
  updateSelectedNavItem,
}) => {
  const chromeConfig = useChromeConfigContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const { isSpoofer } = internalChromeConfig;
  useObserveUrlChange();
  // Adding this back weblab check so that Internal Apps can be visible in internal Ads Console view (when overriding to C). This needs to be removed when internal apps are onboarded to federate

  useEffect(() => {
    // Ensure the ajax call would not be triggered in spoofing mode
    const hasAjaxData = !isSpoofer;
    if (navData) {
      if (hasAjaxData) {
        store.dispatch(initInternalNavStateWithBackupToolbar(navData));
      } else {
        store.dispatch(initInternalNavState(navData));
      }
    }

    const loadAjaxData = async () => {
      if (!hasAjaxData) return;

      try {
        const { globalNav, csrfToken } = await getNavState();
        window.__AAC_FIT_CSRF_TOKEN__ = csrfToken;
        store.dispatch(initAjaxNavState(globalNav));
        NavMetricPublisher.publishCounterMonitor(`ajaxNavStateFailure`, 0);
      } catch (e: any) {
        store.dispatch(updateToolbarItemsWithBackup());
        NavMetricPublisher.publishCounterMonitor(`ajaxNavStateFailure`, 1);
        logger.error('failed to call getNavState:', e);
      }
    };

    loadAjaxData();
  }, []);

  const portalRef = useDomRefById('AACChromePortal');
  // When navData doesn't exist, it's the web component path
  // Use the portal ref inside the web component
  const globalNavPortalRef = !navData ? createRef<HTMLDivElement>() : undefined;
  const navRef = useRef<HTMLDivElement | null>(null);
  const headerSectionRef = useRef<HTMLDivElement | null>(
    document.querySelector(CONSOLE_HEADER_SECTION_ID),
  );

  useEffect(function () {
    markComponentReady(Components.NAV);
  }, []);

  // resize nav on mount.  This scenario happens when the user changes
  // from focus mode to standard mode
  const nav = useCallback((nav) => {
    if (!headerSectionRef.current) {
      headerSectionRef.current = document.querySelector(
        CONSOLE_HEADER_SECTION_ID,
      );
    }
    if (nav) {
      navRef.current = nav;
      if (headerSectionRef.current) {
        const headerSectionRect =
          headerSectionRef.current.getBoundingClientRect();
        nav.style.top = `${headerSectionRect.height}px`;
      }
    }
  }, []);

  useNavLayout(navRef);

  if (!shouldRenderMenu(viewType, chromeConfig.pageLayoutType)) {
    return null;
  }

  if (viewType === VIEW_TYPE.DESKTOP) {
    return (
      <>
        {!navData && (
          <div id="AACChromeGlobalNavPortal" ref={globalNavPortalRef}></div>
        )}
        <PortalContextProvider
          portalElementRef={navData ? portalRef : globalNavPortalRef}
        >
          <SidenavBundleProvider>
            <NavWrapper
              id="AACChromeNav"
              onMouseLeave={() => updateActiveCategory('')}
              ref={nav}
            >
              {toolbarItems && toolbarItems.length > 0 ? (
                <ConsolidatedSideNav
                  toolbarItems={toolbarItems}
                  updateSelectedNavItem={updateSelectedNavItem}
                  {...(!isSpoofer
                    ? {
                        landingAppName: landingAppName,
                        updateLandingAppName: updateLandingAppName,
                      }
                    : {})}
                />
              ) : null}
            </NavWrapper>
            {activeCategoryObject &&
              activeCategoryObject.sidebarType !== SIDEBAR_TYPE.TOOL && (
                <Overlay />
              )}
          </SidenavBundleProvider>
        </PortalContextProvider>
      </>
    );
  }

  return (
    <>
      {!navData && (
        <div id="AACChromeGlobalNavPortal" ref={globalNavPortalRef}></div>
      )}
      <PortalContextProvider
        portalElementRef={navData ? portalRef : globalNavPortalRef}
      >
        <SidenavBundleProvider>
          <MobileNavSheet
            closeButton={{
              type: 'icon',
              label: 'close',
            }}
            onClose={toggleMobileSideNav}
            isOpen={isMobileNavOpen}
            withCloseButton={false}
            type="dark"
            side="start"
            hasInnerPadding={false}
          >
            <ConnectedMobileSideNav />
          </MobileNavSheet>
        </SidenavBundleProvider>
      </PortalContextProvider>
    </>
  );
};

const connector = connect(
  (state: RootState) => {
    return {
      viewType: selectViewType(state),
      isMobileNavOpen: selectIsMobileSideNavOpen(state),
      activeCategoryObject: selectActiveCategoryObject(state),
      toolbarItems: selectToolbarItems(state),
      landingAppName: selectLandingAppName(state),
    };
  },
  (dispatch) => {
    return {
      updateActiveCategory: dispatchUpdateActiveCategory,
      toggleMobileSideNav,
      updateLandingAppName: (category) =>
        dispatch(updateLandingAppName(category)),
      updateSelectedNavItem: (toolbarItemId: string, linkId: string) =>
        dispatch(
          updateSelectedNavItem({
            toolbarItemId,
            linkId,
          }),
        ),
    };
  },
);

export const ConnectedNav = connector(Nav);
